import { Box, Stack, alpha, Button, Typography } from '@mui/material';

import Iconify from 'src/components/iconify';

import { PermissionState, DevicePermission } from './types';

// ----------------------------------------------------------------------

interface PermissionItemProps {
  permission: DevicePermission;
  onGrant: () => void;
  onOpenSettings: () => void;
  isLoading: boolean;
}

// Get status badge configuration
function getStatusConfig(status: PermissionState): {
  label: string;
  color: 'success' | 'error' | 'warning' | 'info';
  icon: string;
} {
  switch (status) {
    case 'granted':
      return { label: 'Granted', color: 'success', icon: 'eva:checkmark-circle-2-fill' };
    case 'denied':
      return { label: 'Denied', color: 'error', icon: 'eva:close-circle-fill' };
    case 'prompt-with-rationale':
      return { label: 'Required', color: 'warning', icon: 'eva:alert-circle-fill' };
    case 'limited':
      return { label: 'Limited', color: 'info', icon: 'eva:alert-triangle-fill' };
    default:
      return { label: 'Required', color: 'warning', icon: 'eva:alert-circle-fill' };
  }
}

// Get border color based on permission status
function getBorderColor(isGranted: boolean, isDenied: boolean): string {
  if (isGranted) return 'success.main';
  if (isDenied) return 'error.light';
  return 'divider';
}

// Get icon background color based on permission status
function getIconBgColor(isGranted: boolean, isDenied: boolean): string {
  if (isGranted) return 'success.lighter';
  if (isDenied) return 'error.lighter';
  return 'grey.100';
}

// Get icon color based on permission status
function getIconColor(isGranted: boolean, isDenied: boolean): string {
  if (isGranted) return 'success.main';
  if (isDenied) return 'error.main';
  return 'text.secondary';
}

// ----------------------------------------------------------------------

export default function PermissionItem({
  permission,
  onGrant,
  onOpenSettings,
  isLoading,
}: PermissionItemProps) {
  const statusConfig = getStatusConfig(permission.status);
  const isGranted = permission.status === 'granted';
  const isDenied = permission.status === 'denied';

  // Compute styles using helper functions to avoid nested ternaries
  const borderColor = getBorderColor(isGranted, isDenied);
  const iconBgColor = getIconBgColor(isGranted, isDenied);
  const iconColor = getIconColor(isGranted, isDenied);

  // Compute background color
  const getBackgroundColor = () => {
    if (isGranted) return (theme: { palette: { success: { main: string } } }) => alpha(theme.palette.success.main, 0.08);
    if (isDenied) return (theme: { palette: { error: { main: string } } }) => alpha(theme.palette.error.main, 0.04);
    return 'background.paper';
  };

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: 2,
        borderColor,
        backgroundColor: getBackgroundColor(),
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        {/* Permission Icon */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: iconBgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify
            icon={permission.icon}
            width={32}
            sx={{
              color: iconColor,
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {permission.name}
            </Typography>

            {/* Status Badge */}
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: `${statusConfig.color}.lighter`,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Iconify
                icon={statusConfig.icon}
                width={14}
                sx={{ color: `${statusConfig.color}.main` }}
              />
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ color: `${statusConfig.color}.dark` }}
              >
                {statusConfig.label}
              </Typography>
            </Box>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              lineHeight: 1.5,
              mb: 1.5,
            }}
          >
            {permission.description}
          </Typography>

          {/* Action Button */}
          {!isGranted && (
            <Button
              variant={isDenied ? 'outlined' : 'contained'}
              color={isDenied ? 'error' : 'primary'}
              size="small"
              onClick={isDenied ? onOpenSettings : onGrant}
              disabled={isLoading}
              startIcon={
                <Iconify
                  icon={isDenied ? 'eva:settings-2-fill' : 'eva:shield-fill'}
                  width={18}
                />
              }
              sx={{
                fontWeight: 600,
                ...(isDenied && {
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  },
                }),
              }}
            >
              {isDenied ? 'Open Settings' : 'Grant Permission'}
            </Button>
          )}

          {isGranted && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Iconify icon="eva:checkmark-fill" width={16} sx={{ color: 'success.main' }} />
              <Typography variant="caption" color="success.main" fontWeight={600}>
                Permission granted
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
